import { useMemo } from "react"
import { cva } from "class-variance-authority"
import UploadIcon from "./Assets/upload.svg"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import Spinner from "../Spinner"
import cn from "../../../../utils/classes"
import ColumnContainer from "../Layout/ColumnContainer"
import Label from "../Label"
import Typography, { typographyTypes } from "../Typography"

export interface DropzoneProps {
  label?: string
  isRequired?: boolean
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  isDisabled?: boolean
  styleOverrides?: string
  isLoading?: boolean
  errorMessage?: string
  isImageUploadOnly?: boolean
}

export const Dropzone: React.FC<DropzoneProps> = ({
  label,
  isRequired = false,
  onFileChange,
  isDisabled = false,
  styleOverrides,
  isLoading = false,
  errorMessage,
  isImageUploadOnly = false,
}) => {
  const { cms: cmsContent } = useCMSReady(["common"])
  const commonText = cmsContent.common

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault() // Necessary to allow for drop
  }

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      onFileChange({
        target: { files: event.dataTransfer.files },
      } as React.ChangeEvent<HTMLInputElement>)
    }
  }

  //handler for onChange that wraps the input function and resets the element value to allow for multiple of same file entry
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Copy the selected files synchronously so upstream async handlers don't
    // observe a cleared input or a pooled React SyntheticEvent.
    const filesCopy = event.target.files ? Array.from(event.target.files) : null

    // Create a minimal synthetic event object that contains the files copy.
    // This avoids passing the original React event through async/await chains
    // where React may pool and nullify it, and it also protects against
    // the native input being cleared (value = "") before upstream reads it.
    const safeEvent = {
      target: {
        files: filesCopy as unknown,
      },
    } as React.ChangeEvent<HTMLInputElement>

    onFileChange(safeEvent)

    // Clear the native input value so the same file can be selected again.
    // Capture the input element reference now (synchronously) so we can clear
    // it after handing off the files — don't rely on the React event later.
    const inputEl = event.currentTarget as HTMLInputElement | null
    if (inputEl) {
      // Delay clearing to avoid interfering with any synchronous handlers.
      // Using setTimeout 0 yields a microtask that runs after current call stack.
      setTimeout(() => {
        try {
          inputEl.value = ""
        } catch {
          // ignore clearing errors
        }
      }, 0)
    }
  }

  const variants = cva(
    [
      "flex",
      "flex-col",
      "text-center",
      "items-center",
      "justify-center",
      "w-full",
      "h-40",
      "border-[2px]",
      "border-dashed",
      "rounded-lg",
      "focus:border-primary-blue",
      "focus:border-solid",
      "focus:outline-none",
    ],
    {
      variants: {
        isDisabled: {
          true: "border-gray-200 bg-gray-100 cursor-not-allowed",
          false: "border-gray-400 cursor-pointer bg-gray-50 hover:bg-gray-100",
        },
        isLoading: {
          true: "border-gray-400 cursor-not-allowed focus:none hover:bg-gray-50 focus:border-gray-400 focus:border-dashed",
          false: "",
        },
        error: {
          true: "border-red-500 border-solid",
          false: "",
        },
      },
    },
  )

  const hasError = !!errorMessage
  const className = useMemo(() => {
    return cn(
      "box-border",
      variants({ isDisabled, isLoading, error: hasError }),
      styleOverrides,
    )
  }, [isDisabled, isLoading, styleOverrides, variants, hasError])

  const isDisabledOrLoading = isDisabled || isLoading

  return (
    <ColumnContainer styleOverrides="w-full space-y-2">
      {!!label && (
        <Label
          text={label}
          htmlFor={`dropdown-input-${label}`}
          isRequired={isRequired}
          size="sm"
          fontWeight="medium"
        />
      )}
      <label
        className={className}
        tabIndex={0}
        role="button"
        aria-disabled={isDisabledOrLoading}
        onDragOver={!isDisabledOrLoading ? handleDragOver : undefined}
        onDrop={!isDisabledOrLoading ? handleDrop : undefined}
        onKeyDown={(event) => {
          if (
            (event.key === "Enter" || event.key === " ") &&
            !isDisabledOrLoading
          ) {
            event.preventDefault()
            const input = event.currentTarget.querySelector(
              'input[type="file"]',
            ) as HTMLInputElement
            if (input) input.click()
          }
        }}
      >
        {isLoading ? (
          <Spinner containerStyleOverrides="h-[100px] w-[100px]" text="" />
        ) : isDisabled ? (
          <div>{commonText?.uploadDisabled ?? ""}</div>
        ) : (
          <div className="flex flex-col space-y-1  items-center justify-center w-[70%]">
            <div className="flex justify-center items-center">
              <img
                className="w-5 h-5 pb-1"
                src={UploadIcon}
                alt="upload icon"
              />
            </div>
            <p
              className="mb-0 text-sm text-gray-500 dark:text-gray-400"
              id="dropzone-instructions"
            >
              {commonText?.uploadDragAndDrop ?? ""}
            </p>
            <p className="mb-0 mt-8 text-sm text-gray-500 dark:text-gray-400">
              {isImageUploadOnly
                ? (commonText?.uploadFileTypesImageOnly ?? "")
                : (commonText?.uploadFileTypes ?? "")}
            </p>
            <div className="text-xs text-external-link-blue underline">
              {commonText?.uploadBrowse ?? ""}
            </div>
          </div>
        )}
        <input
          id={`dropdown-input-${label}`}
          disabled={isDisabled || isLoading}
          type="file"
          className="hidden"
          onChange={handleOnChange}
          aria-describedby="dropzone-instructions"
        />
      </label>
      <Typography text={errorMessage || ""} variant={typographyTypes.ERROR} />
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {!!errorMessage ? errorMessage : ""}
      </div>
    </ColumnContainer>
  )
}
